import { FC, useState } from "react";
import { Box, Typography } from "@mui/material";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { useTranslation } from "react-i18next";
import { useUnverifiedProperties } from "./useUnverifiedProperties";
import { VerificationModal } from "./VerificationModal";

/**
 * MS-484 — the top-bar nudge shown while the host has ≥1 property that isn't
 * published (unverified MT permit). Visibility is backend-driven
 * (`requiresMtVerification && hasUnverifiedProperties`); the copy varies by
 * count. Not dismissible. "وثّق الآن" opens the shared verification modal —
 * which goes straight to the card for a single property, or a picker grid when
 * several need verifying.
 */
export const VerificationHeaderAlert: FC = () => {
  const { t } = useTranslation();
  const { featureOn, count } = useUnverifiedProperties();
  const [open, setOpen] = useState(false);

  if (!featureOn) return null;

  const n = count || 1;
  const key =
    n === 1 ? "ALERT_ONE" : n === 2 ? "ALERT_TWO" : n <= 10 ? "ALERT_FEW" : "ALERT_MANY";
  const message = t(`PERMIT_VERIFICATION.${key}`, { n });

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          backgroundColor: "#FEF9E7",
          border: "1px solid #F7E3A1",
          borderRadius: "999px",
          px: "8px",
          py: "6px",
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            backgroundColor: "#FBE7A2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <PriorityHighIcon sx={{ fontSize: 16, color: "#B45309" }} />
        </Box>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "#3A2E00",
            whiteSpace: { xs: "normal", sm: "nowrap" },
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {message}
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          sx={{
            backgroundColor: "#5B3FFF",
            color: "#fff",
            border: "none",
            borderRadius: "999px",
            px: "16px",
            py: "8px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
            "&:hover": { backgroundColor: "#4930CC" },
          }}
        >
          {t("PERMIT_VERIFICATION.ALERT_CTA")}
        </Box>
      </Box>
      <VerificationModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default VerificationHeaderAlert;
